import axios from "axios";
import {
  TOKEN_KEY, OUTLET_KEY, STORAGE_BACKEND,
  getStoredToken, getStoredOutlet, clearStoredToken, clearStoredOutlet,
} from "@/lib/authStorage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const http = axios.create({ baseURL: API });

http.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const t = getStoredToken();
  // Whitespace-token defense (matches SessionContext.headers()):
  // never construct `Authorization: Bearer ` for a blank token.
  if (typeof t === "string" && t.trim().length > 0) {
    config.headers.Authorization = `Bearer ${t.trim()}`;
  }
  const o = getStoredOutlet();
  if (o) config.headers["X-Selected-Outlet-Id"] = o;
  return config;
});

// -----------------------------------------------------------------
// Auth telemetry — PII-safe. Emits one console log line whenever the
// interceptor is about to blow away the session so ops can grep prod
// browser logs (or Sentry / whatever) for the trigger distribution.
//
// Fields are booleans + short strings only. No token, no JWT claims,
// no operator email. `route` is the request path (server-owned, safe
// to log). `token_storage` records which backend held the token when
// the interceptor fired — useful after the sessionStorage→localStorage
// migration to prove the new storage is doing its job.
// -----------------------------------------------------------------
function logAuthLogoutTrigger({ error_code, route, had_token,
                                retry_attempted, retry_succeeded }) {
  try {
    console.warn("bulk_capture_auth_logout_trigger", {
      event: "bulk_capture_auth_logout_trigger",
      error_code,
      route,
      had_token,
      token_storage: STORAGE_BACKEND,
      retry_attempted,
      retry_succeeded,
    });
  } catch { /* never let telemetry break the request */ }
}

// Ambiguous 401 codes get a single jittered retry before we blow away
// the session. This class of error is *most often* a transient
// Control Room hiccup (deploy, restart, DB blip). Immediate logout on
// the first flake was iter_23's operator-facing pain.
const RETRYABLE_401_CODES = new Set(["cr_unauthorized"]);
// Codes below are ALWAYS a definitive rejection — retry cannot help,
// only a fresh SSO cycle can.
const TERMINAL_401_CODES = new Set(["token_missing", "token_expired", "token_invalid"]);

const RETRY_SAFE_METHODS = new Set(["get", "head", "options"]);

const _retriedRequestFlag = "__bc_auth_retried__";
const jitteredDelay = () => 300 + Math.floor(Math.random() * 500); // 300..800ms

function doHardLogout({ detail, code, route, had_token, retry_attempted, retry_succeeded }) {
  logAuthLogoutTrigger({
    error_code: code, route, had_token,
    retry_attempted, retry_succeeded,
  });
  clearStoredToken();
  clearStoredOutlet();
  const url = detail?.sso_login_url;
  if (url) {
    const redirect = encodeURIComponent(window.location.origin + "/");
    setTimeout(() => { window.location.href = `${url}&redirect_uri=${redirect}`; }, 50);
  }
}

// Auto-redirect to CR SSO on 401 token errors, with one jittered
// retry for the ambiguous `cr_unauthorized` case.
http.interceptors.response.use(
  (r) => r,
  async (err) => {
    if (err?.response?.status !== 401) return Promise.reject(err);
    const detail = err?.response?.data?.detail;
    const code = detail?.error_code;
    const cfg = err.config || {};
    const route = cfg.url || "unknown";
    const had_token = !!getStoredToken();

    if (TERMINAL_401_CODES.has(code)) {
      doHardLogout({ detail, code, route, had_token,
                     retry_attempted: false, retry_succeeded: false });
      return Promise.reject(err);
    }

    if (RETRYABLE_401_CODES.has(code)) {
      // Guard: only retry once per originating request; never retry
      // unsafe/non-idempotent verbs.
      const method = (cfg.method || "get").toLowerCase();
      const alreadyRetried = cfg[_retriedRequestFlag] === true;
      const safeVerb = RETRY_SAFE_METHODS.has(method);
      if (!alreadyRetried && safeVerb) {
        await new Promise((r) => setTimeout(r, jitteredDelay()));
        try {
          const retryCfg = { ...cfg, [_retriedRequestFlag]: true };
          const resp = await http(retryCfg);
          // Success on retry — telemetry only, no logout.
          logAuthLogoutTrigger({
            error_code: code, route, had_token,
            retry_attempted: true, retry_succeeded: true,
          });
          return resp;
        } catch (retryErr) {
          const retryCode = retryErr?.response?.data?.detail?.error_code;
          // If the retry hit the same auth-error class, THEN logout.
          if (retryErr?.response?.status === 401 &&
              (TERMINAL_401_CODES.has(retryCode) || RETRYABLE_401_CODES.has(retryCode))) {
            doHardLogout({
              detail: retryErr?.response?.data?.detail || detail,
              code: retryCode || code,
              route, had_token,
              retry_attempted: true, retry_succeeded: false,
            });
          }
          return Promise.reject(retryErr);
        }
      }
      // Non-retryable (mutation or already retried): keep pre-retry
      // behavior — hard logout.
      doHardLogout({ detail, code, route, had_token,
                     retry_attempted: false, retry_succeeded: false });
    }
    return Promise.reject(err);
  }
);

export const api = {
  me: () => http.get("/control-room/me").then((r) => r.data),
  ssoLoginUrl: () => http.get("/control-room/sso/login-url").then((r) => r.data),
  validateContext: () => http.post("/control-room/context/validate", {}).then((r) => r.data),

  createBatch: (notes) => http.post("/bulk-capture/batches", { notes }).then((r) => r.data),
  listBatches: () => http.get("/bulk-capture/batches").then((r) => r.data),
  getBatch: (id) => http.get(`/bulk-capture/batches/${id}`).then((r) => r.data),
  uploadFiles: (batchId, files, autoProcess = true) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    return http
      .post(
        `/bulk-capture/batches/${batchId}/files?auto_process=${autoProcess}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((r) => r.data);
  },
  startProcessing: (batchId) =>
    http.post(`/bulk-capture/batches/${batchId}/process`).then((r) => r.data),
  listCandidates: (params = {}) =>
    http.get("/bulk-capture/candidates", { params }).then((r) => r.data),
  candidateCounts: (batchId) =>
    http
      .get("/bulk-capture/candidates/counts", {
        params: batchId ? { batch_id: batchId } : {},
      })
      .then((r) => r.data),
  getCandidate: (id) => http.get(`/bulk-capture/candidates/${id}`).then((r) => r.data),
  updateCandidate: (id, body) =>
    http.put(`/bulk-capture/candidates/${id}`, body).then((r) => r.data),
  retryOcr: (id) =>
    http.post(`/bulk-capture/candidates/${id}/retry-ocr`).then((r) => r.data),
  // --- Operator OCR recovery — "Extract Missing Details" ---
  // Re-runs OCR on the candidate's existing image and merges numeric
  // line-item fields without overwriting operator edits. Pure rescue
  // tool; never creates/modifies an EC bill.
  extractMissingDetails: (id, mode = "fill_blank_only") =>
    http
      .post(`/bulk-capture/candidates/${id}/extract-missing-details`, { mode })
      .then((r) => r.data),
  rejectCandidate: (id, reason) =>
    http.post(`/bulk-capture/candidates/${id}/reject`, { reason }).then((r) => r.data),
  // The Expense Capture handoff is RETIRED. 1OS Bills is the canonical supplier-Bill
  // authority: a reviewed candidate is confirmed in Bills (/bills/:candidateId), never
  // posted to EC. No client method targets the retired send-to-ec route.
  // Next / previous candidate in the SAME queue filter. Powers
  // auto-advance after Send to EC / Reject. Returns
  // `{candidate_id, remaining_count}`; candidate_id is null when the
  // queue is drained beyond the current candidate.
  getNextInQueue: (id, params = {}) =>
    http.get(`/bulk-capture/candidates/${id}/next`, { params }).then((r) => r.data),
  // Phase 1 manual bill grouping.
  groupCandidates: ({ candidate_ids, page_order }) =>
    http.post(`/bulk-capture/candidates/group`, { candidate_ids, page_order }).then((r) => r.data),
  ungroupCandidate: (id) =>
    http.post(`/bulk-capture/candidates/${id}/ungroup`).then((r) => r.data),
  reorderPages: (id, page_order) =>
    http.patch(`/bulk-capture/candidates/${id}/pages/reorder`, { page_order }).then((r) => r.data),
  attachmentUrl: (id) => `${API}/bulk-capture/attachment/${id}`,
  candidateTrace: (id) => http.get(`/bulk-capture/candidates/${id}/trace`).then((r) => r.data),
  ecMeta: () => http.get("/bulk-capture/_meta").then((r) => r.data),
  // EC Units proxy. Backend caches 5 min per (org, outlet). Frontend
  // should call once per CandidateReview mount; never crashes on a
  // degraded response (units:[]) — Combobox falls back to free-text.
  listUnits: () => http.get("/bulk-capture/units").then((r) => r.data),
  searchVendors: (q = "") => http.get("/vendors", { params: { q } }).then((r) => r.data),
  createVendor: (body) => http.post("/vendors", body).then((r) => r.data),
  listBills: () => http.get("/bills").then((r) => r.data),
  getBill: (id) => http.get(`/bills/${id}`).then((r) => r.data),

  // --- Validation Dashboard ---
  validationMetrics: (params = {}) =>
    http.get("/bulk-capture/validation/metrics", { params }).then((r) => r.data),
  validationDrilldown: (params = {}) =>
    http.get("/bulk-capture/validation/drilldown", { params }).then((r) => r.data),
  validationCsvUrl: (params = {}) => {
    const sp = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") sp.set(k, v);
    });
    const qs = sp.toString();
    return `${API}/bulk-capture/validation/export.csv${qs ? `?${qs}` : ""}`;
  },
};
